import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../ui/button';
import type { AssetLabel } from '../../types/campaign';
import type { DriveFileSummary } from './types';

// Full-size preview modal for a Drive asset. Reusable across Phase 1
// (Campaign editor + view) and Phase 2 (Build View).
//
// Preview path: a single Drive `/preview` iframe handles every file type
// (images, videos, PDFs, docs). Drive's player resolves auth, codec, and
// rendering uniformly -- much simpler than the previous mime-branched
// path that fell over for videos because their webContentLink required
// an authed redirect.
//
// Navigation: ← / → buttons + Left/Right arrow keys cycle through the
// `files` list. The grid passes its filtered live-files set (orphan
// ghost cards are excluded — they have nothing to preview).
// Dismiss: Cancel button, ESC, or backdrop click.
//
// Label display: when the current file has an authored label in
// `labels`, the header shows it as read-only text below the filename.
// The lightbox is always read-only for labels — editing happens on the
// card grid.

type Props = {
  files: DriveFileSummary[];
  initialIndex: number;
  labels: AssetLabel[];
  onClose: () => void;
};

const DRIVE_PREVIEW_BASE = 'https://drive.google.com/file/d';

function clampIndex(index: number, length: number): number {
  if (length === 0) return 0;
  if (index < 0) return 0;
  if (index >= length) return length - 1;
  return index;
}

export function DriveLightbox({ files, initialIndex, labels, onClose }: Props) {
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  const [index, setIndex] = useState<number>(() => clampIndex(initialIndex, files.length));

  // Keep the index in range if the parent's `files` shrinks while the
  // lightbox is open (e.g. a refresh that drops a deleted file). No
  // auto-close — the AM keeps preview open on whatever is still there.
  useEffect(() => {
    setIndex((prev) => clampIndex(prev, files.length));
  }, [files.length]);

  const file = files[index] ?? null;
  const labelByFileId = useMemo(() => {
    const map = new Map<string, string>();
    for (const entry of labels) map.set(entry.driveFileId, entry.label);
    return map;
  }, [labels]);
  const labelText = file ? labelByFileId.get(file.id) ?? '' : '';

  const atFirst = index <= 0;
  const atLast = index >= files.length - 1;

  useEffect(() => {
    closeBtnRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'ArrowLeft' && !atFirst) {
        setIndex((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === 'ArrowRight' && !atLast) {
        setIndex((i) => Math.min(files.length - 1, i + 1));
        return;
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, atFirst, atLast, files.length]);

  if (!file) {
    // The grid only opens the lightbox when there is at least one file
    // and a valid initialIndex — defence-in-depth so a stale state
    // mismatch doesn't crash the modal.
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-5xl rounded-md bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="drive-lightbox-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-3">
          <div className="min-w-0 flex-1">
            <h2
              id="drive-lightbox-title"
              className="truncate text-base font-semibold text-slate-900"
              title={file.name}
            >
              {file.name}
            </h2>
            {labelText ? (
              <p className="truncate text-sm text-slate-700" title={labelText}>
                <span className="text-slate-500">Label:</span> {labelText}
              </p>
            ) : (
              <p className="text-xs text-slate-500">{file.mimeType}</p>
            )}
          </div>
          <button
            type="button"
            ref={closeBtnRef}
            onClick={onClose}
            aria-label="Close preview"
            className="ml-4 rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            ✕
          </button>
        </div>

        <div className="bg-slate-50 p-3">
          <iframe
            // Including the file id in the title so React's diff swaps
            // the iframe DOM node when the index changes -- avoids
            // rare cases where Drive's embedded player keeps state
            // (paused video position) across `src` swaps.
            key={file.id}
            title={`Drive preview: ${file.name}`}
            src={`${DRIVE_PREVIEW_BASE}/${file.id}/preview`}
            className="h-[70vh] w-full rounded border border-slate-200 bg-white"
            allow="autoplay; fullscreen"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-5 py-3">
          <button
            type="button"
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            disabled={atFirst}
            aria-label="Previous asset"
            title={atFirst ? 'First asset' : 'Previous asset'}
            className="inline-flex items-center rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            ← Prev
          </button>

          <div className="flex items-center gap-2">
            {file.webContentLink && (
              <a
                href={file.webContentLink}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="inline-flex items-center rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
              >
                Download
              </a>
            )}
            <a
              href={file.webViewLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
            >
              Open in Drive
            </a>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>

          <button
            type="button"
            onClick={() => setIndex((i) => Math.min(files.length - 1, i + 1))}
            disabled={atLast}
            aria-label="Next asset"
            title={atLast ? 'Last asset' : 'Next asset'}
            className="inline-flex items-center rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}
